import React, { useRef } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { getFileSizeString } from '@modernloop/shared/utils';
import { AttachmentIcon } from '@modernloop/shared/icons';
import { Alert, Button, Chip, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { find, first, sortBy } from 'lodash';

import { ATTACHMENT_UPLOAD_ERROR_CODE } from 'src/components/AddAttachments/attachmentUploadError';

import { FileStatus, FileUploadFlow, UIFile, removeUploadFile, startFileUpload } from 'src/slices/files';

import { useDispatch, useSelector } from 'src/store';

interface AddAttachmentProps {
  flow: FileUploadFlow;
  disabled?: boolean;
  /**
   * When set (only meaningful for {@link FileUploadFlow.INTERVIEWER_EVENT}), scopes the picker to a
   * single event's attachment sub-bucket instead of the flat per-flow bucket. All other flows ignore it.
   */
  eventId?: string;
}

const MAX_ATTACHMENT_LIMIT = 20 * 1024 * 1024; // 20 MB
const SINGLE_ATTACHMENT_LIMIT = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_ATTACHMENT_FILE_TYPES = [
  'image/*', // all images
  'application/pdf', // pdf
  'application/msword', // doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.ms-excel', // xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-powerpoint', // ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  'text/csv', // csv
  '.csv',
];

type UIFileMap = { [filename: string]: UIFile };

/**
 * Stable empty sub-bucket for the missing per-event case so the selector does not return a fresh
 * object each render (which would churn re-renders).
 */
const EMPTY_FILE_MAP: UIFileMap = {};

/** Renders the shared attachment picker and upload state for communication editors. */
// eslint-disable-next-line modernloop/validate-component-definition.cjs
export default function AddAttachment({ flow, disabled = false, eventId }: AddAttachmentProps): JSX.Element | null {
  const { t } = useTranslation();
  // For INTERVIEWER_EVENT with an eventId, read the per-event sub-bucket; every other surface reads the
  // flat per-flow bucket unchanged.
  const dictFiles = useSelector((state) => {
    if (eventId != null && flow === FileUploadFlow.INTERVIEWER_EVENT) {
      return state.fileUpload[flow][eventId] ?? EMPTY_FILE_MAP;
    }
    return state.fileUpload[flow];
  });
  const dispatch = useDispatch();
  const files = sortBy(dictFiles, 'timestamp') as UIFile[];
  const existingAttachmentSize = files.reduce((total, file) => total + file.size, 0);
  const [error, setError] = React.useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const showError = (message: string) => {
    // emptying the input value to allow the user to upload the file again after the error
    if (fileRef.current) {
      fileRef.current.value = '';
    }
    setError(message);
  };

  const fileOnChange = (e) => {
    const file = first(e.target.files) as File;
    if (!file) {
      return;
    }
    if (find(files, { name: file.name })) {
      setError(t('hiring-dashboard-common:common.attachment.duplicateError', { fileName: file.name }));
      return;
    }
    if (file.size > SINGLE_ATTACHMENT_LIMIT) {
      showError(t('hiring-dashboard-common:common.attachment.exceedsSingleLimit'));
      return;
    }
    if (existingAttachmentSize + file.size > MAX_ATTACHMENT_LIMIT) {
      showError(t('hiring-dashboard-common:common.attachment.exceedsTotalLimit'));
      return;
    }
    dispatch(startFileUpload(flow, file, eventId));
    // clear out input value
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const removeFile = (file: UIFile) => {
    dispatch(removeUploadFile(flow, file.name, eventId));
  };

  const formatFileLabel = (file: UIFile) =>
    t('hiring-dashboard-common:common.attachment.fileWithSize', {
      fileName: file.name,
      fileSize: getFileSizeString(file.size),
    });

  const formatUploadingLabel = (file: UIFile) =>
    t('hiring-dashboard-common:common.attachment.uploadingWithSize', {
      fileName: file.name,
      fileSize: getFileSizeString(file.size),
    });

  const formatUploadError = (file: UIFile) => {
    const errorCode = file.errorCode ?? ATTACHMENT_UPLOAD_ERROR_CODE.UNKNOWN;
    const errorMessage = t(`hiring-dashboard-common:common.attachment.uploadErrorMessages.${errorCode}`, {
      defaultValue: t('hiring-dashboard-common:common.attachment.uploadErrorMessages.UNKNOWN'),
    });

    return t('hiring-dashboard-common:common.attachment.uploadErrorWithCode', {
      fileName: file.name,
      errorCode,
      errorMessage,
    });
  };

  const renderChip = (file: UIFile) => {
    if (file.status === FileStatus.UPLOADING) {
      // In-flight upload: disabled progress chip, no delete affordance yet.
      return (
        <Chip
          key={file.name}
          size="small"
          disabled
          icon={<CircularProgress size={14} />}
          label={formatUploadingLabel(file)}
        />
      );
    }
    if (file.status === FileStatus.ERRORED) {
      // Failed upload: keep the delete affordance so the user can remove and retry.
      return (
        <Chip
          key={file.name}
          size="small"
          color="error"
          icon={<AttachmentIcon />}
          label={formatUploadError(file)}
          onDelete={disabled ? undefined : () => removeFile(file)}
        />
      );
    }
    return (
      <Chip
        key={file.name}
        size="small"
        icon={<AttachmentIcon />}
        label={formatFileLabel(file)}
        onDelete={disabled ? undefined : () => removeFile(file)}
      />
    );
  };

  return (
    <Stack spacing={1} alignItems="flex-start">
      {files.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {files.map(renderChip)}
        </Stack>
      )}
      {!disabled && (
        <Button component="label" size="small">
          {t('hiring-dashboard-common:common.attachment.addAttachment')}
          <input
            type="file"
            accept={ALLOWED_ATTACHMENT_FILE_TYPES.join(',')}
            ref={fileRef}
            onChange={fileOnChange}
            hidden
          />
        </Button>
      )}
      {(files.length > 0 || !disabled) && (
        <Typography variant="caption" color="text.secondary">
          {files.length > 0
            ? t('hiring-dashboard-common:common.attachment.totalWithLimit', {
                totalSize: getFileSizeString(existingAttachmentSize),
              })
            : t('hiring-dashboard-common:common.attachment.sizeLimit')}
        </Typography>
      )}
      <Snackbar open={error.length > 0} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Stack>
  );
}
